import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAllUserRoles,
  setFetchedUsers,
  setInputValue,
  setSelectedRole,
  setSelectedUserId,
} from "store/ticketFetchSlice";
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { fetchUsersWithId } from "app/Api Calls/TeamsRequiredApis";
import { searchTeams } from "app/Api Calls/SearchTeams";
import fetchRoles from "app/Api Calls/Roles";
import { jwtService } from "app/auth/jwtService";
import settingsConfig from "app/configs/settingConfig";

function UserFilterSelect() {
  const dispatch = useDispatch();
  const currentUser = jwtService.getUser();
  const isAdmin = settingsConfig.roles.admin.includes(currentUser.roleName);
  const inputValue = useSelector((state) => state.ticketFetchSlice.inputValue);
  const fetchedUsers = useSelector(
    (state) => state.ticketFetchSlice.fetchedUsers
  );
  const selectedRole = useSelector(
    (state) => state.ticketFetchSlice.selectedRole
  );
  const allUsersRoles = useSelector(
    (state) => state.ticketFetchSlice.allUsersRoles || []
  );
  const ticketsLoading = useSelector(
    (state) => state.ticketFetchSlice.ticketsLoading
  );

  // Pagination state
  const [userPage, setUserPage] = useState(0);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [totalUserPages, setTotalUserPages] = useState(1);
  const pageSize = 100;
  const lastInputValue = useRef("");

  useEffect(() => {
    const fetchAllRoles = async () => {
      try {
        const roles = await fetchRoles();
        dispatch(setAllUserRoles(roles));
      } catch (error) {
        console.error("Failed to fetch roles:", error);
      }
    };

    fetchAllRoles();
  }, [dispatch]);

  // Admins can search the entire user base by typing a last name.
  useEffect(() => {
    if (!isAdmin) return;

    if (inputValue.length > 2) {
      // Reset page if input changes
      setUserPage(0);
      setHasMoreUsers(true);
      lastInputValue.current = inputValue;

      const fetchAllUsers = async () => {
        try {
          const response = await fetchUsersWithId(0, pageSize, {
            lastName: inputValue,
          });
          const adaptedUsers = response.elements.map((user) => ({
            ...user,
            roleName: user.roleCollection?.[0]?.roleName ?? "",
          }));
          dispatch(setFetchedUsers(adaptedUsers));
          setHasMoreUsers(response.elements.length === pageSize);
          setTotalUserPages(response.totalPages || 1);
        } catch (error) {
          console.error("Failed to fetch users:", error);
        }
      };

      fetchAllUsers();
    } else {
      dispatch(setFetchedUsers([]));
      setHasMoreUsers(false);
      setTotalUserPages(1);
    }
  }, [inputValue, dispatch, isAdmin]);

  // Supervisors and Team Leaders only see users belonging to their own teams,
  // mirroring the team scoping already applied to the "Ομάδα" filter.
  useEffect(() => {
    if (isAdmin) return;

    const fetchTeamUsers = async () => {
      try {
        const response = await searchTeams(0, 100, null, null, null, null, currentUser.id, null);
        const teams = response?.elements ?? [];
        const usersById = new Map();
        teams.forEach((team) => {
          (team.userCollection ?? []).forEach((user) => {
            usersById.set(user.id, {
              ...user,
              roleName: user.roleCollection?.[0]?.roleName ?? "",
            });
          });
        });
        dispatch(setFetchedUsers(Array.from(usersById.values())));
        setHasMoreUsers(false);
        setTotalUserPages(1);
      } catch (error) {
        console.error("Failed to fetch team users:", error);
      }
    };

    fetchTeamUsers();
  }, [dispatch, isAdmin, currentUser.id]);

  // Fetch a specific page of users (for page controls)
  const fetchUsersPage = async (page) => {
    setIsFetchingMore(true);
    try {
      const response = await fetchUsersWithId(page, pageSize, {
        lastName: lastInputValue.current,
      });
      const adaptedUsers = response.elements.map((user) => ({
        ...user,
        roleName: user.roleCollection?.[0]?.roleName ?? "",
      }));
      dispatch(setFetchedUsers(adaptedUsers));
      setUserPage(page);
      setHasMoreUsers(adaptedUsers.length === pageSize);
      setTotalUserPages(response.totalPages || 1);
    } catch (error) {
      setHasMoreUsers(false);
      console.error("Failed to fetch users for page:", error);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const handleUserChange = (_, value) => {
    dispatch(setSelectedUserId(value ? value.id : ""));
    dispatch(setSelectedRole(""));
  };

  const handleInputChange = (_, newInputValue) => {
    dispatch(setInputValue(newInputValue));
  };

  function formatRoleName(roleName) {
    const parts = (roleName || "").replace("ROLE_", "").split("_");
    const formattedName = parts
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
    return formattedName || "—";
  }

  // Sort by group label (role), then last name, then first name
  function groupAndSortUsers(users) {
    const withRoles = users.map((user) => {
      const roleName =
        user.roleCollection?.[0]?.roleName ?? user.roleName ?? "";
      return {
        ...user,
        roleName,
        _groupLabel: formatRoleName(roleName),
      };
    });

    withRoles.sort(
      (a, b) =>
        a._groupLabel.localeCompare(b._groupLabel, undefined, {
          sensitivity: "base",
        }) ||
        (a.lastName ?? "").localeCompare(b.lastName ?? "", undefined, {
          sensitivity: "base",
        }) ||
        (a.firstName ?? "").localeCompare(b.firstName ?? "", undefined, {
          sensitivity: "base",
        })
    );

    return withRoles;
  }

  const sortedUsers = groupAndSortUsers(fetchedUsers);
  const filteredUsers = selectedRole
    ? sortedUsers.filter((user) => user.roleName === selectedRole)
    : sortedUsers;

  // Custom ListboxComponent with sticky page controls at the bottom
  const ListboxComponent = React.forwardRef(function ListboxComponent(
    props,
    ref
  ) {
    const { children, ...other } = props;
    const listRef = useRef();

    useEffect(() => {
      if (ref) {
        if (typeof ref === "function") {
          ref(listRef.current);
        } else {
          ref.current = listRef.current;
        }
      }
    }, [ref]);

    const renderPageNumbers = () => {
      const pages = [];
      for (let i = 0; i < totalUserPages; i++) {
        pages.push(
          <button
            key={i}
            onMouseDown={(e) => e.preventDefault()} // keep popup open
            onClick={(e) => {
              e.stopPropagation();
              if (userPage !== i) fetchUsersPage(i);
            }}
            style={{
              margin: 2,
              padding: "2px 8px",
              borderRadius: 4,
              border: userPage === i ? "2px solid #1976d2" : "1px solid #ccc",
              background: userPage === i ? "#e3f2fd" : "#fff",
              color: userPage === i ? "#1976d2" : "#333",
              cursor: userPage === i ? "default" : "pointer",
              fontWeight: userPage === i ? 600 : 400,
              outline: "none",
            }}
            disabled={userPage === i || isFetchingMore}
          >
            {i + 1}
          </button>
        );
      }

      return (
        <li
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            background: "white",
            padding: "8px 8px",
            borderTop: "1px solid #eee",
            textAlign: "center",
          }}
          aria-disabled
          onMouseDown={(e) => e.preventDefault()}
        >
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>
            Σελίδα {userPage + 1} από {totalUserPages}
          </div>
          <div>{pages}</div>
        </li>
      );
    };

    return (
      <ul
        {...other}
        ref={listRef}
        style={{ maxHeight: 300, overflow: "auto", paddingBottom: 0 }}
      >
        {isFetchingMore && (
          <li style={{ textAlign: "center", padding: 8 }}>Φόρτωση...</li>
        )}
        {children}
        {renderPageNumbers()}
      </ul>
    );
  });

  return (
    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
      <FormControl
        variant="outlined"
        style={{ width: "20%", marginRight: "0px" }}
      >
        <InputLabel htmlFor="role-select">Ρόλος</InputLabel>
        <Select
          label="Ρόλος"
          value={selectedRole}
          disabled={ticketsLoading}
          onChange={(e) => dispatch(setSelectedRole(e.target.value))}
          inputProps={{
            name: "role",
            id: "role-select",
          }}
          style={{ height: "42px" }}
        >
          <MenuItem value="">
            <em>Όλοι</em>
          </MenuItem>
          {allUsersRoles.map((role) => (
            <MenuItem value={role.roleName} key={role.id}>
              {formatRoleName(role.roleName)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Autocomplete
        sx={{ width: "80%", height: "42px" }}
        id="user-autocomplete"
        options={filteredUsers}
        disabled={ticketsLoading}
        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
        groupBy={(option) => formatRoleName(option.roleName)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Χρήστης"
            variant="outlined"
            disabled={ticketsLoading}
            size="small"
            style={{ height: "42px" }}
          />
        )}
        renderOption={(props, user) => (
          <li {...props} key={`${user.teamId}-${user.id}`}>
            {`${user.firstName} ${user.lastName}`}
          </li>
        )}
        inputValue={inputValue}
        freeSolo
        onInputChange={handleInputChange}
        noOptionsText="Δεν βρέθηκε χρήστης"
        onChange={handleUserChange}
        ListboxComponent={ListboxComponent}
      />
    </div>
  );
}

export default UserFilterSelect;
